/**
 * Resolution of a glTF's relative asset URIs to dataset-relative paths.
 *
 * A `.gltf` names its external buffers and textures by URI relative to the
 * glTF file itself. Roboto files are addressed by id, not path, so each URI
 * has to be turned back into a dataset-relative path before it can be looked
 * up. These helpers are the pure half of that translation; `ModelLoader`
 * supplies the dataset context.
 */

/**
 * True for URIs that name a separate file to fetch, as opposed to inline
 * data or an already-absolute location.
 *
 * `data:` URIs carry their payload inline and need no resolution. Absolute
 * URLs (`https://…`, and protocol-relative `//host/…`) already point
 * somewhere reachable and are left for Babylon to fetch directly.
 */
export function isRelativeAssetUri(uri: string): boolean {
  if (uri === "") {
    return false;
  }
  if (uri.startsWith("//")) {
    return false;
  }
  // A scheme per RFC 3986: letter followed by letters, digits, +, -, or .
  return !/^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(uri);
}

/**
 * Resolves a glTF-relative URI to a dataset-relative path.
 *
 * Joins `uri` against the directory containing `gltfRelativePath` and
 * normalizes `.` and `..` segments, so a nested model can reach a shared
 * mesh directory (`a/b/robot.gltf` + `../meshes/g.stl` → `a/meshes/g.stl`).
 *
 * Returns `null` when the URI cannot name a file in this dataset: when it
 * escapes the dataset root via `..`, when it is absolute or inline (see
 * {@link isRelativeAssetUri}), or when it resolves to an empty path. A
 * `null` is a rejection, not a "not found" — the caller should refuse to
 * load rather than fall back, since a traversal escape is the shape a
 * malicious asset would take.
 *
 * URI-decodes each segment, because glTF URIs are percent-encoded while
 * Roboto relative paths are not.
 */
export function resolveSiblingPath(
  gltfRelativePath: string,
  uri: string,
): string | null {
  if (!isRelativeAssetUri(uri)) {
    return null;
  }

  // Strip any query or fragment; neither is meaningful for a dataset path.
  const withoutQuery = uri.split(/[?#]/)[0];
  if (withoutQuery === "") {
    return null;
  }

  const uriSegments = withoutQuery.split("/");

  // The URI has to end in a real name to denote a file. A trailing "", "."
  // or ".." names a directory, which resolves to a valid-looking path
  // ("a/b/../" → "a") that is nonetheless not an asset.
  const lastSegment = uriSegments[uriSegments.length - 1];
  if (lastSegment === "" || lastSegment === "." || lastSegment === "..") {
    return null;
  }

  const directorySegments = gltfRelativePath.split("/").slice(0, -1);
  const resolved: string[] = [...directorySegments];

  for (const rawSegment of uriSegments) {
    let segment: string;
    try {
      segment = decodeURIComponent(rawSegment);
    } catch {
      // Malformed percent-encoding: not a path we can resolve.
      return null;
    }

    if (segment === "" || segment === ".") {
      continue;
    }
    if (segment === "..") {
      if (resolved.length === 0) {
        // Escapes the dataset root.
        return null;
      }
      resolved.pop();
      continue;
    }
    resolved.push(segment);
  }

  if (resolved.length === 0) {
    return null;
  }

  return resolved.join("/");
}
